import type { MetricRecord } from "./MetricRecord";

/**
 * One node in the metric tree, keyed by its dot-path prefix. `T` is the per-node
 * payload.
 *
 * Child ids live in the index (reach them via
 * `getChildren`), so the node carries no recursive `children[]`.
 */
export interface MetricTreeNode<T = undefined> {
  /** Full dotted path that identifies this node (e.g. `"imu.accel.x"`). */
  id: string;
  /** Last dotted segment (e.g. `"x"`). */
  label: string;
  /** 0-based depth from the root. */
  depth: number;
  /** `id` is a published metric name */
  isLeaf: boolean;
  /** Has at least one child */
  hasChildren: boolean;
  /** Count of leaf metrics at or under this node. */
  leafCount: number;
  /** Per-node payload */
  data: T;
}

/**
 * The id-addressed index for accessing the metric tree.
 *
 * Constructed via {@link buildMetricTree}
 */
export interface MetricTreeIndex<T = undefined> {
  /** Id of the synthetic root; its children are the top-level namespaces. */
  rootItemId: string;
  /** The node for an id, or `undefined` if the id isn't in the index. Arrow-typed
   *  (not a method) so consumers can pass it by reference without `this` binding. */
  getItem: (id: string) => MetricTreeNode<T> | undefined;
  /** Ordered child ids of an id (`rootItemId` → the top-level namespaces). */
  getChildren: (id: string) => string[];
}

const ROOT = "";

/** Last dotted segment of a path (`"a.b.c"` → `"c"`; `"a"` → `"a"`). */
function lastSegment(path: string): string {
  return path.slice(path.lastIndexOf(".") + 1);
}

/** Internal build node: the public fields plus child ids (the tree edges). */
interface BuildNode<T> {
  id: string;
  label: string;
  depth: number;
  isLeaf: boolean;
  leafCount: number;
  data: T;
  childIds: string[];
}

/**
 * Build a {@link MetricTreeIndex} from a flat list of dot-delimited metric names.
 * Structure is name-derived; `dataForPath` optionally decorates each node with a
 * payload (called for every node, branches included).
 *
 *  - `isLeaf` is true iff the node's path is one of `names`.
 *  - `leafCount = (isLeaf ? 1 : 0) + Σ children.leafCount`.
 *  - Siblings sort by last segment alphabetically with `numeric` collation (`x2` before `x10`).
 *
 * API contract: metric names are unique (the server rejects duplicates), so this
 * does not de-duplicate. Empty whole-string names are skipped.
 *
 * O(names · depth)
 */
export function buildMetricTree<T = undefined>(
  names: string[],
  dataForPath: (path: string) => T = () => undefined as T,
): MetricTreeIndex<T> {
  const nameSet = new Set(names);
  const nodesById = new Map<string, BuildNode<T>>();

  const root: BuildNode<T> = {
    id: ROOT,
    label: "",
    depth: -1,
    isLeaf: false,
    leafCount: 0,
    data: dataForPath(ROOT),
    childIds: [],
  };
  nodesById.set(ROOT, root);

  for (const name of names) {
    if (name === "") {
      continue; // names are unique by API contract; only empties are guarded
    }
    let path = "";
    let parent = root;
    const segments = name.split(".");
    for (let depth = 0; depth < segments.length; depth++) {
      path = path === ROOT ? segments[depth] : `${path}.${segments[depth]}`;
      let node = nodesById.get(path);
      if (node === undefined) {
        node = {
          id: path,
          label: segments[depth],
          depth,
          isLeaf: nameSet.has(path),
          leafCount: 0,
          data: dataForPath(path),
          childIds: [],
        };
        nodesById.set(path, node);
        parent.childIds.push(path);
      }
      parent = node;
    }
  }

  // Sort each node's children (numeric-aware) and roll up `leafCount` bottom-up.
  const sortAndCount = (childIds: string[]): number => {
    childIds.sort((a, b) =>
      lastSegment(a).localeCompare(lastSegment(b), undefined, {
        numeric: true,
      }),
    );
    let total = 0;
    for (const id of childIds) {
      const node = nodesById.get(id)!;
      node.leafCount = (node.isLeaf ? 1 : 0) + sortAndCount(node.childIds);
      total += node.leafCount;
    }
    return total;
  };
  root.leafCount = sortAndCount(root.childIds);

  const toNode = (node: BuildNode<T>): MetricTreeNode<T> => ({
    id: node.id,
    label: node.label,
    depth: node.depth,
    isLeaf: node.isLeaf,
    hasChildren: node.childIds.length > 0,
    leafCount: node.leafCount,
    data: node.data,
  });

  return {
    rootItemId: ROOT,
    getItem: (id) => {
      const node = nodesById.get(id);
      return node === undefined ? undefined : toNode(node);
    },
    getChildren: (id) => nodesById.get(id)?.childIds ?? [],
  };
}

/**
 * Recursively visit every descendant of `id` (skipping `id` itself), pre-order,
 * passing each node and its walk-relative depth.
 */
export function walk<T>(
  index: MetricTreeIndex<T>,
  id: string,
  visit: (node: MetricTreeNode<T>, depth: number) => void,
  depth = 0,
): void {
  for (const childId of index.getChildren(id)) {
    const node = index.getItem(childId);
    if (node !== undefined) {
      visit(node, depth);
      walk(index, childId, visit, depth + 1);
    }
  }
}

/**
 * Every dotted prefix of `path`, root-first, excluding `path` itself — the
 * branches to expand to reveal the node at `path`. `"imu.accel.x"` →
 * `["imu", "imu.accel"]`; an un-dotted path → `[]`.
 */
export function ancestorPaths(path: string): string[] {
  const segments = path.split(".");
  const ancestors: string[] = [];
  for (let i = 1; i < segments.length; i++) {
    ancestors.push(segments.slice(0, i).join("."));
  }
  return ancestors;
}

/**
 * The most recently published record in `records`, or `undefined` when empty. A
 * leaf folds every record sharing its name; the table and the detail card both
 * surface this single newest record's value, timestamp, and attribution.
 */
export function latestRecord(
  records: MetricRecord[],
): MetricRecord | undefined {
  if (records.length === 0) {
    return undefined;
  }
  let best = records[0];
  for (let i = 1; i < records.length; i++) {
    if (records[i].published > best.published) {
      best = records[i];
    }
  }
  return best;
}
